import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  VStack,
  HStack,
  Button,
  Grid,
  GridItem,
  Badge,
} from '@chakra-ui/react';
import {
  FaTractor,
  FaArrowRight,
  FaCheckCircle,
  FaHandshake,
  FaChartLine,
  FaSeedling,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { GiCoffeeBeans, GiFarmTractor, GiPlantWatering } from 'react-icons/gi';

import PageHero from '../Components/PageHero';
import SectionHeading from '../Components/SectionHeading';
import Reveal from '../Components/Reveal';

import agroHeroImage from '../Components/Assets/macadamia.webp';
import agroServiceImage from '../Components/Assets/Agrooa.webp';

function AgroBusiness() {
  const services = [
    {
      icon: GiCoffeeBeans,
      title: 'Culture de Cacaoyer',
      description:
        "Culture principale sur plusieurs centaines d'hectares. Production de cacao de haute qualité pour les marchés nationaux et internationaux.",
      color: 'brand.500',
      bg: 'brand.50',
    },
    {
      icon: FaSeedling,
      title: 'Culture de Macadamia',
      description:
        'Culture de rente secondaire en plantation mixte. Diversification des revenus et valorisation des terres agricoles.',
      color: 'gold.600',
      bg: 'gold.50',
    },
    {
      icon: FaTractor,
      title: 'Transformation',
      description:
        "Transition de la production brute vers la transformation locale pour maximiser les marges bénéficiaires à l'export.",
      color: 'navy.600',
      bg: 'navy.50',
    },
    {
      icon: FaHandshake,
      title: 'Partenariats Locaux',
      description:
        'Partenariat étroit avec les agriculteurs et les coopératives locales pour augmenter la productivité.',
      color: 'brand.600',
      bg: 'brand.50',
    },
    {
      icon: FaChartLine,
      title: 'Valeur Ajoutée',
      description:
        'Création de valeur ajoutée locale et diversification des revenus pour les communautés.',
      color: 'gold.600',
      bg: 'gold.50',
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Sites de Production',
      description:
        "Province de la Tshopo (Bafwasende) et Province de l'Ituri (Irumu, Village Komanda).",
      color: 'navy.600',
      bg: 'navy.50',
    },
  ];

  const avantages = [
    'Plantation mixte Cacaoyer (principal) + Macadamia (secondaire)',
    "Plusieurs centaines d'hectares en exploitation",
    'Partenariats avec les coopératives locales',
    'Transformation en produits finis haut de gamme',
    "Création d'emplois stables et respectueux de l'environnement",
    "Présence dans les provinces de la Tshopo et de l'Ituri",
  ];

  const sites = [
    {
      title: "Province de la Tshopo",
      text: 'Territoire de Bafwasende',
      icon: GiPlantWatering,
      from: 'brand.600',
      to: 'brand.800',
    },
    {
      title: "Province de l'Ituri",
      text: "Territoire d'Irumu (Village Komanda)",
      icon: GiPlantWatering,
      from: 'brand.500',
      to: 'brand.700',
    },
  ];

  return (
    <Box>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <PageHero
        eyebrow="Pôle Agrobusiness — Export & Transformation"
        title="Cacao & Macadamia,"
        accent="l'Excellence Agricole"
        description={
          <>
            TRIMER SARL développe une exploitation agricole de grande envergure sur plusieurs
            centaines d'hectares. Notre modèle de culture associe le{' '}
            <strong>cacaoyer</strong> (culture principale) et le{' '}
            <strong>macadamia</strong> (culture de rente secondaire).
          </>
        }
        image={agroHeroImage}
        imagePosition="center"
        icon={GiFarmTractor}
        gradient="linear(120deg, rgba(5,42,27,0.97) 0%, rgba(11,89,58,0.85) 50%, rgba(8,28,52,0.88) 100%)"
        badges={[{ label: 'Cacaoyer' }, { label: 'Macadamia' }, { label: 'Export' }]}
        cta={{ label: 'Nous contacter', to: '/contact', secondary: { label: 'Nos services', to: '#services' } }}
      />

      {/* ═══════════════════════ SERVICES ═══════════════════════ */}
      <Box id="services" py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="7xl">
          <SectionHeading
            eyebrow="Notre modèle"
            title="Ce que nous faisons"
            lead="Une exploitation agricole moderne alliant culture principale et secondaire pour une production durable et rentable."
          />

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {services.map((service, index) => (
              <Reveal key={service.title} delay={(index % 3) * 0.08}>
                <Box
                  bg="gray.50"
                  p={8}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.100"
                  h="100%"
                  transition="all 0.35s"
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'card', bg: 'white', borderColor: 'transparent' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    h="56px"
                    w="56px"
                    borderRadius="xl"
                    bg={service.bg}
                    mb={5}
                  >
                    <Icon as={service.icon} boxSize={6} color={service.color} />
                  </Box>
                  <Heading size="md" color="gray.900" mb={3}>
                    {service.title}
                  </Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.8">
                    {service.description}
                  </Text>
                </Box>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ SITES ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="gray.50" position="relative" overflow="hidden">
        <Box position="absolute" inset={0} className="bg-grid-dark" opacity={0.5} pointerEvents="none" />

        <Container maxW="7xl" position="relative">
          <SectionHeading
            eyebrow="Nos sites"
            title="Sites de Production"
            lead="Une implantation stratégique au cœur des bassins agricoles de l'est de la RDC."
          />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {sites.map((site, index) => (
              <Reveal key={site.title} delay={index * 0.1}>
                <Box
                  bgGradient={`linear(135deg, ${site.from}, ${site.to})`}
                  p={{ base: 9, md: 12 }}
                  borderRadius="2xl"
                  color="white"
                  position="relative"
                  overflow="hidden"
                  boxShadow="card"
                  transition="all 0.35s"
                  _hover={{ transform: 'translateY(-6px)', boxShadow: 'lift' }}
                >
                  <Icon
                    as={site.icon}
                    position="absolute"
                    top={-6}
                    right={-6}
                    boxSize="150px"
                    color="whiteAlpha.200"
                  />
                  <Icon as={FaMapMarkerAlt} boxSize={9} mb={5} color="gold.300" />
                  <Heading size="lg" mb={2}>
                    {site.title}
                  </Heading>
                  <Text opacity={0.9} fontSize="sm">
                    {site.text}
                  </Text>
                </Box>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ AVANTAGES ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="7xl">
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 12, lg: 16 }} alignItems="center">
            <GridItem>
              <Reveal>
                <VStack align="start" spacing={7}>
                  <SectionHeading
                    align="start"
                    eyebrow="Pourquoi nous choisir"
                    title="Nos avantages"
                    mb={0}
                  />

                  <VStack align="stretch" spacing={3} w="100%">
                    {avantages.map((avantage) => (
                      <HStack key={avantage} spacing={4} align="start">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          h="28px"
                          w="28px"
                          borderRadius="full"
                          bg="brand.50"
                          flexShrink={0}
                        >
                          <Icon as={FaCheckCircle} color="brand.500" boxSize={4} />
                        </Box>
                        <Text color="gray.700" fontSize="sm" lineHeight="1.7">
                          {avantage}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>

                  <Button as={RouterLink} to="/contact" size="lg" rightIcon={<FaArrowRight />}>
                    Demander un devis
                  </Button>
                </VStack>
              </Reveal>
            </GridItem>

            <GridItem>
              <Reveal delay={0.12}>
                <Box
                  h={{ base: '300px', md: '440px' }}
                  bgImage={`url(${agroServiceImage})`}
                  bgSize="cover"
                  bgPosition="center"
                  borderRadius="2xl"
                  position="relative"
                  overflow="hidden"
                  boxShadow="lift"
                >
                  <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(5,42,27,0.9), rgba(5,42,27,0.2))" />
                  <VStack position="absolute" bottom={6} left={6} right={6} align="start" spacing={1}>
                    <Text fontWeight="800" fontSize="lg" color="white">
                      Cacao & Macadamia
                    </Text>
                    <Text fontSize="sm" color="whiteAlpha.800">
                      Production locale d'excellence
                    </Text>
                  </VStack>
                </Box>
              </Reveal>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════ CTA ═══════════════════════ */}
      <Box py={{ base: 16, md: 20 }} bg="white">
        <Container maxW="4xl">
          <Reveal>
            <Box
              borderRadius="3xl"
              bgGradient="linear(120deg, brand.600, brand.800 55%, navy.800)"
              color="white"
              px={{ base: 8, md: 14 }}
              py={{ base: 12, md: 16 }}
              textAlign="center"
              position="relative"
              overflow="hidden"
              boxShadow="lift"
            >
              <Box position="absolute" inset={0} className="bg-grid" opacity={0.3} />
              <Box
                position="absolute"
                bottom="-90px"
                left="-60px"
                w="260px"
                h="260px"
                borderRadius="full"
                bgGradient="radial(circle at center, rgba(224,174,56,0.32), transparent 70%)"
              />

              <VStack spacing={6} position="relative">
                <Badge bg="whiteAlpha.200" color="white" px={4} py={1.5} fontSize="xs" letterSpacing="0.12em">
                  Agriculture
                </Badge>
                <Heading size="xl" color="white" fontWeight="800">
                  Vous avez un projet agricole ?
                </Heading>
                <Text opacity={0.9} maxW="xl">
                  Notre équipe est prête à vous accompagner dans la production, la
                  transformation et la commercialisation de cacao et macadamia.
                </Text>
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  bg="white"
                  color="brand.800"
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                >
                  Contactez-nous
                </Button>
              </VStack>
            </Box>
          </Reveal>
        </Container>
      </Box>
    </Box>
  );
}

export default AgroBusiness;
